'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { LayoutGroup, motion } from 'framer-motion'
import React, { forwardRef, useId } from 'react'

import { TouchTarget } from '../Button'
import { Link } from '../Link'

export const Sidebar = ({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) => {
  return (
    <nav
      {...props}
      className={clsx(className, 'flex h-full min-h-0 flex-col')}
    />
  )
}

export const SidebarHeader = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-b border-gray-900/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
      )}
    />
  )
}

export const SidebarBody = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8',
      )}
    />
  )
}

export const SidebarFooter = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-gray-900/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
      )}
    />
  )
}

export const SidebarSection = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
  const id = useId()
  return (
    <LayoutGroup id={id}>
      <div
        {...props}
        data-slot="section"
        className={clsx(className, 'flex flex-col gap-0.5')}
      />
    </LayoutGroup>
  )
}

export const SidebarDivider = ({ className, ...props }: React.ComponentPropsWithoutRef<'hr'>) => {
  return (
    <hr
      {...props}
      className={clsx(className, 'my-4 border-t border-gray-900/5 lg:-mx-4 dark:border-white/5')}
    />
  )
}

export const SidebarSpacer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, 'mt-8 flex-1')}
    />
  )
}

export const SidebarHeading = ({ className, ...props }: React.ComponentPropsWithoutRef<'h3'>) => {
  return (
    <h3
      {...props}
      className={clsx(className, 'mb-1 px-2 text-xs/6 font-medium text-gray-500 dark:text-gray-400')}
    />
  )
}

export const SidebarItem = forwardRef(function SidebarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | Omit<Headless.ButtonProps, 'as' | 'className'>
    | Omit<Headless.ButtonProps<typeof Link>, 'as' | 'className'>
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const classes = clsx(
    // Base
    'transition-all flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-gray-900 sm:py-2 sm:text-sm/5',
    // Leading icon/icon-only
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-5',
    // Trailing icon (down chevron or similar)
    '*:last:data-[slot=icon]:ml-auto *:last:data-[slot=icon]:size-5 sm:*:last:data-[slot=icon]:size-4',
    // Avatar
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 sm:*:data-[slot=avatar]:size-6',
    // Hover
    'data-hover:bg-gray-900/5',
    // Focus
    'focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500',
    // Dark mode
    'dark:text-white dark:data-hover:bg-white/5',
    // Disabled
    'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
  )

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-y-2 -left-4 w-0.5 rounded-full bg-gray-900 dark:bg-white"
        />
      )}
      {'to' in props ? (
        <Headless.CloseButton
          as={Link}
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          aria-current={current ? 'page' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.CloseButton>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('cursor-pointer', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  )
})

export const SidebarLabel = ({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      {...props}
      className={clsx(className, 'truncate')}
    />
  )
}
